import api from './api';

// Types
export interface Tier {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  price_monthly?: number;
  price_yearly?: number;
  is_active: boolean;
  sort_order: number;
  signup_credits: number;
  monthly_credits: number;
  bonus_credits: number;
  bonus_start_date?: string;
  bonus_end_date?: string;
  bonus_description?: string;
  bonus_is_active: boolean;
  button_text?: string;
  button_url?: string;
  is_button_enabled: boolean;
  icon?: string;
  roi_text_1?: string;
  roi_text_2?: string;
  show_roi_texts: boolean;
  user_count?: number;
  monthly_revenue?: number;
  created_at: string;
  updated_at: string;
  // Computed properties
  is_bonus_currently_active?: boolean;
  total_signup_credits?: number;
}

export interface TierCreate {
  name: string;
  display_name: string;
  description?: string;
  price_monthly?: number;
  price_yearly?: number;
  signup_credits: number;
  monthly_credits: number;
  sort_order: number;
}

export interface TierUpdate {
  display_name?: string;
  description?: string;
  price_monthly?: number;
  price_yearly?: number;
  is_active?: boolean;
  sort_order?: number;
  signup_credits?: number;
  monthly_credits?: number;
  bonus_credits?: number;
  bonus_start_date?: string;
  bonus_end_date?: string;
  bonus_description?: string;
  bonus_is_active?: boolean;
  button_text?: string;
  button_url?: string;
  is_button_enabled?: boolean;
  icon?: string;
  roi_text_1?: string;
  roi_text_2?: string;
  show_roi_texts?: boolean;
}

export interface FeatureCategory {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  icon?: string;
  sort_order: number;
}

export interface TierLimit {
  id: string;
  tier_id: string;
  feature_category_id: string;
  feature_key: string;
  feature_display_name: string;
  feature_description?: string;
  limit_value: number; // -1 for unlimited
  reset_period: 'monthly' | 'daily' | 'never';
  is_active: boolean;
}

export interface TierLimitCheck {
  allowed: boolean;
  limit_value: number;
  current_usage: number;
  remaining: number;
  reset_period: string;
  next_reset?: string;
}

export interface BulkTierLimitUpdate {
  tier_id: string;
  limits: Record<string, {
    limit_value: number;
    reset_period: string;
    is_active: boolean;
  }>;
}

export interface TierMigration {
  from_tier_id: string;
  to_tier_id: string;
  user_ids: string[];
  preserve_usage: boolean;
  grant_credits: boolean;
}

const tierService = {
  // Tier Management
  getAllTiers: async (activeOnly: boolean = false): Promise<Tier[]> => {
    const response = await api.get<Tier[]>(`/tiers/?active_only=${activeOnly}`);
    return response.data;
  },

  // Public tier access (no authentication required)
  getPublicTiers: async (activeOnly: boolean = true): Promise<Tier[]> => {
    const response = await api.get<Tier[]>(`/tiers/public?active_only=${activeOnly}`);
    return response.data;
  },

  // Health check
  checkTierSystemHealth: async (): Promise<any> => {
    const response = await api.get('/tiers/health');
    return response.data;
  },

  // Debug endpoint
  debugTiers: async (): Promise<any> => {
    const response = await api.get('/tiers/debug');
    return response.data;
  },

  getTier: async (tierId: string): Promise<Tier> => {
    const response = await api.get<Tier>(`/tiers/${tierId}`);
    return response.data;
  },

  createTier: async (data: TierCreate): Promise<Tier> => {
    const response = await api.post<Tier>('/tiers/', data);
    return response.data;
  },

  updateTier: async (tierId: string, data: TierUpdate): Promise<Tier> => {
    const response = await api.put<Tier>(`/tiers/${tierId}`, data);
    return response.data;
  },

  deleteTier: async (tierId: string): Promise<void> => {
    await api.delete(`/tiers/${tierId}`);
  },

  // Feature Categories
  getFeatureCategories: async (): Promise<FeatureCategory[]> => {
    const response = await api.get<FeatureCategory[]>('/tiers/feature-categories/');
    return response.data;
  },

  // Tier Limits
  getTierLimits: async (tierId: string): Promise<TierLimit[]> => {
    const response = await api.get<TierLimit[]>(`/tiers/${tierId}/limits/`);
    return response.data;
  },

  updateTierLimit: async (limitId: string, data: Partial<TierLimit>): Promise<TierLimit> => {
    const response = await api.put<TierLimit>(`/tiers/limits/${limitId}`, data);
    return response.data;
  },

  bulkUpdateTierLimits: async (data: BulkTierLimitUpdate): Promise<TierLimit[]> => {
    const response = await api.post<TierLimit[]>('/tiers/limits/bulk-update', data);
    return response.data;
  },

  // Usage Management
  checkUserLimit: async (userId: string, featureKey: string): Promise<TierLimitCheck> => {
    const response = await api.get<TierLimitCheck>(`/tiers/users/${userId}/check-limit/${featureKey}`);
    return response.data;
  },

  // Check current user's limit (simpler endpoint)
  checkCurrentUserLimit: async (featureKey: string): Promise<TierLimitCheck> => {
    const response = await api.get<TierLimitCheck>(`/tiers/check-limit/${featureKey}`);
    return response.data;
  },

  incrementUserUsage: async (userId: string, featureKey: string, amount: number = 1): Promise<{ success: boolean }> => {
    const response = await api.post<{ success: boolean }>(`/tiers/users/${userId}/increment-usage/${featureKey}?amount=${amount}`);
    return response.data;
  },

  resetUserUsage: async (userId: string, featureKey?: string): Promise<{ success: boolean }> => {
    const url = `/tiers/users/${userId}/reset-usage${featureKey ? `?feature_key=${featureKey}` : ''}`;
    const response = await api.post<{ success: boolean }>(url);
    return response.data;
  },

  // User Tier Management
  assignUserToTier: async (userId: string, tierId: string, grantSignupCredits: boolean = true): Promise<{ success: boolean }> => {
    const response = await api.post<{ success: boolean }>(`/tiers/users/${userId}/assign-tier/${tierId}?grant_signup_credits=${grantSignupCredits}`);
    return response.data;
  },

  migrateUsersBetweenTiers: async (migration: TierMigration): Promise<any> => {
    const response = await api.post('/tiers/migrate-users', migration);
    return response.data;
  },

  grantMonthlyCredits: async (userId: string): Promise<{ success: boolean; credits_granted: number; new_balance: number }> => {
    const response = await api.post(`/tiers/users/${userId}/grant-monthly-credits`);
    return response.data;
  },

  // Analytics
  getTierAnalytics: async (): Promise<any[]> => {
    const response = await api.get('/tiers/analytics/stats');
    return response.data;
  },

  // Helper methods for integration with existing systems
  checkFeatureAccess: async (featureKey: string): Promise<boolean> => {
    try {
      // Get current user from auth context or API
      const userResponse = await api.get('/users/profile');
      const user = userResponse.data;
      
      if (!user.id) return false;
      
      const limitCheck = await tierService.checkUserLimit(user.id, featureKey);
      return limitCheck.allowed;
    } catch (error) {
      console.error('Error checking feature access:', error);
      return false; // Fail closed
    }
  },

  trackFeatureUsage: async (featureKey: string, amount: number = 1): Promise<boolean> => {
    try {
      // Get current user from auth context or API
      const userResponse = await api.get('/users/profile');
      const user = userResponse.data;
      
      if (!user.id) return false;
      
      const result = await tierService.incrementUserUsage(user.id, featureKey, amount);
      return result.success;
    } catch (error) {
      console.error('Error tracking feature usage:', error);
      return false;
    }
  },

  // Bonus credits utility functions
  isBonusActive: (tier: Tier): boolean => {
    if (!tier.bonus_is_active || tier.bonus_credits <= 0) {
      return false;
    }
    
    const now = new Date();
    
    // Check start date
    if (tier.bonus_start_date && now < new Date(tier.bonus_start_date)) {
      return false;
    }
    
    // Check end date
    if (tier.bonus_end_date && now > new Date(tier.bonus_end_date)) {
      return false;
    }
    
    return true;
  },

  getTotalSignupCredits: (tier: Tier): number => {
    let total = tier.signup_credits;
    if (tierService.isBonusActive(tier)) {
      total += tier.bonus_credits;
    }
    return total;
  },

  formatBonusEndDate: (endDate: string): string => {
    const date = new Date(endDate);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) {
      return 'Expired';
    } else if (diffDays === 1) {
      return 'Ends tomorrow';
    } else if (diffDays <= 7) {
      return `Ends in ${diffDays} days`;
    } else {
      return `Ends ${date.toLocaleDateString()}`;
    }
  }
};

export default tierService;